// frontend/src/components/billing/PlansGrid.tsx
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 *
 * Grid de planos (Fase 3 — billing). Fonte de verdade: banco via
 * GET /api/billing/plans. Reutilizado na PricingPage (landing) e no
 * checkout do workspace.
 */

import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Button, Chip } from '@mui/material';
import { VERDE_MENTA, withAlpha } from '../../themes/themeConstants';
import { Plan, BillingCycle, billingService } from '../../services/billingService';

interface PlansGridProps {
  plans: Plan[];
  ciclo: BillingCycle;
  /** Plano atualmente assinado (destacado e com botão desabilitado) */
  currentPlanId?: string;
  ctaLabel?: string;
  onSelect: (plan: Plan) => void;
  loadingPlanId?: string | null;
}

const PlansGrid: React.FC<PlansGridProps> = ({
  plans,
  ciclo,
  currentPlanId,
  ctaLabel = 'Começar Agora',
  onSelect,
  loadingPlanId = null,
}) => {
  return (
    <Grid container spacing={3}>
      {plans.map((plan) => {
        const preco = ciclo === 'MONTHLY' ? plan.preco_mensal : plan.preco_anual;
        const isCurrent = plan.id === currentPlanId;
        const recursos = plan.recursos || {};

        const features = [
          billingService.formatLimit(recursos.max_agentes, 'agente de IA', 'agentes de IA', 'Agentes de IA ilimitados'),
          billingService.formatLimit(recursos.max_mensagens, 'mensagem/mês', 'mensagens/mês', 'Mensagens ilimitadas'),
        ];
        if (plan.trial_dias > 0) {
          features.push(`${plan.trial_dias} dias de teste grátis`);
        }

        return (
          <Grid item xs={12} sm={6} md={3} key={plan.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: isCurrent
                  ? `1px solid ${VERDE_MENTA}`
                  : `0.5px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                backgroundColor: isCurrent ? withAlpha(VERDE_MENTA, 0.08) : 'transparent',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${withAlpha(VERDE_MENTA, 0.15)}`,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="h6" align="center" sx={{ textTransform: 'capitalize' }}>
                    {plan.nome}
                  </Typography>
                  {isCurrent && (
                    <Chip label="Atual" size="small" sx={{ backgroundColor: withAlpha(VERDE_MENTA, 0.2), color: VERDE_MENTA }} />
                  )}
                </Box>

                <Box textAlign="center" my={2}>
                  <Typography variant="h4" component="span" sx={{ color: VERDE_MENTA }}>
                    {billingService.formatPrice(preco)}
                  </Typography>
                  {preco > 0 && (
                    <Typography variant="subtitle2" component="span" color="text.secondary">
                      {ciclo === 'MONTHLY' ? '/mês' : '/ano'}
                    </Typography>
                  )}
                </Box>

                {plan.descricao && (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    {plan.descricao}
                  </Typography>
                )}

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, flexGrow: 1 }}>
                  {features.map((feature, i) => (
                    <Box component="li" key={i} sx={{ mb: 1 }}>
                      <Typography variant="body2">✓ {feature}</Typography>
                    </Box>
                  ))}
                </Box>

                <Button
                  variant={isCurrent ? 'outlined' : 'contained'}
                  fullWidth
                  disabled={isCurrent || loadingPlanId !== null}
                  onClick={() => onSelect(plan)}
                  sx={{ mt: 2 }}
                >
                  {isCurrent ? 'Plano atual' : loadingPlanId === plan.id ? 'Processando...' : ctaLabel}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PlansGrid;
